import { BulbOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Button, Input, Modal, Tooltip, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { MultipleUserSearch } from '@/components/multiple-user-search/multiple-user-search.component'
import {
  GetThanksPostDocument,
  GetTimelinePostsDocument,
  PostType,
  RequestedPostFragment,
  UserInfoFragment,
  usePostBatchCreateMutation,
  usePostUpdateMutation,
} from '@/generated/graphql'

import s from './thanks.module.less'

interface ThanksMessageComponentProps {
  receiver?: UserInfoFragment
  post?: RequestedPostFragment
  onClose: () => void
}

export const ThanksMessageComponent: React.FC<ThanksMessageComponentProps> = ({ receiver, post, onClose }) => {
  const { t } = useTranslation()
  const [message, setMessage] = React.useState<string>(post ? post.content : '')

  const [users, setUsers] = React.useState<{ userId: any; avatarUrl: string; name: string }[]>(
    receiver ? [{ userId: receiver.userId, avatarUrl: receiver.avatarUrl, name: receiver.name }] : []
  )

  const [postCreate, { loading }] = usePostBatchCreateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!, getOperationName(GetThanksPostDocument)!],
  })

  const [postUpdate, { loading: updateLoading }] = usePostUpdateMutation()

  const messageInput = (
    <Input.TextArea
      placeholder={t('post.thanks.showThanks')}
      autoSize={false}
      value={message}
      onChange={(e) => {
        const currStr = e.target.value
        if ((currStr.match(/\n/g) || []).length >= 6) {
          return
        }
        setMessage(currStr)
      }}
      rows={6}
      showCount
      maxLength={160}
    />
  )

  const tip = users.length !== 0 && (
    <Tooltip title={t('post.thanks.sendTo1') + users.map((user) => user.name).join(', ') + t('post.thanks.sendTo2')}>
      <BulbOutlined style={{ color: '#FFBB5C' }} />
    </Tooltip>
  )

  const buttons = (
    <div className={s.buttons}>
      <Button disabled={loading || updateLoading} onClick={() => onClose()}>
        {t('post.cancel')}
      </Button>
      <Button
        disabled={!message || users.length === 0}
        type='primary'
        loading={loading || updateLoading}
        onClick={async () => {
          if (post) {
            await postUpdate({
              variables: {
                postId: post.postId,
                post: { content: message, receiverUserId: post.receiver?.userId || 0, postType: PostType.Thanks },
              },
            })
          } else {
            await postCreate({
              variables: {
                post: { content: message, receiverUserId: receiver?.userId, postType: PostType.Thanks },
                receiverUserIds: users.map((user) => user.userId),
              },
            })
          }
          onClose()
        }}
      >
        {post ? t('post.save') : t('post.postOnTimeline')}
      </Button>
    </div>
  )

  return (
    <div className={s.message}>
      {!post && (
        <Typography.Title level={5}>
          {t('post.thanks.whoToThanks')}
          {tip}
        </Typography.Title>
      )}
      {!post && <MultipleUserSearch value={users} onChange={setUsers} excludeViewer={true} defaultUser={receiver} />}
      <Typography.Title level={5}>{t('post.thanks.messageTitle')}</Typography.Title>
      {messageInput}
      {buttons}
    </div>
  )
}

export const ThanksMessageModal: React.FC<{ receiver?: UserInfoFragment; post?: RequestedPostFragment }> = ({
  receiver,
  post,
  children,
  ...props
}) => {
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()

  children = React.cloneElement(children as any, {
    ...props,
    onClick: toggle,
  })

  return (
    <>
      {children}
      <Modal
        visible={visible}
        onClose={() => toggle()}
        onCancel={() => toggle()}
        footer={null}
        maskClosable={false}
        destroyOnClose
        title={t('post.thanks.sendThanks')}
      >
        <ThanksMessageComponent post={post} receiver={receiver} onClose={() => toggle()} />
      </Modal>
    </>
  )
}
